import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { PEOPLE_ROSTER_AREA } from '../../../common/constants';
import { GET_MY_ROSTER_SHIFTS } from '../../../common/graphql/people/roster';
import { useUserContext } from '../../../common/utils/context';
import { isMyDayEnabledForUser, isMyDayFeatureEnabledForTenant } from '../../../common/utils/myDay/access';
import { getLocalTime } from '../../../common/utils/time/time';
import useTodayDateKey from '../../hooks/useTodayDateKey';
import { isLegacyMobileUi } from '../../utils/legacyMobileUi';
import { GET_MY_DAY_ASSIGNED_RUN_CHECK } from './driverMyDayQueries';

const APP_RUNTIME = {
	IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
};

export const MY_DAY_MODE = Object.freeze({
	CLASSIC: 'classic',
	DRIVER: 'driver',
	PICKER: 'picker',
	HUB: 'hub',
});

export const MY_DAY_TICKET_SCOPE = Object.freeze({
	DRIVER: 'driver',
	GENERIC: 'generic',
});

const useMyDayMode = () => {
	const {
		user,
		tenant,
		isDriver,
		isPicker,
		isAVC,
		tenantSettings,
		refetch: refetchUser,
	} = useUserContext();
	// The redesigned driver flow replaced the classic one, so the per-user My day
	// opt-in only applies to users kept on the legacy screens; everyone else
	// follows the tenant gate.
	const canAccessMyDayFeature = isLegacyMobileUi(user)
		? isMyDayEnabledForUser({
			user,
			tenantSettings,
			isAVC,
			isDevelopment: APP_RUNTIME.IS_DEVELOPMENT,
		})
		: Boolean(user) && isMyDayFeatureEnabledForTenant({
			tenantSettings,
			isAVC,
			isDevelopment: APP_RUNTIME.IS_DEVELOPMENT,
		});
	// Rolls over at midnight and on returning to the app: the app shell stays mounted overnight.
	const todayDateKey = useTodayDateKey(tenant?.timezone);
	const {
		data: rosterData,
		loading: rosterLoading,
		error: rosterError,
		refetch: refetchRosterShifts,
	} = useQuery(GET_MY_ROSTER_SHIFTS, {
		variables: {
			fromDate: todayDateKey,
			toDate: todayDateKey,
		},
		skip: !isAVC || !canAccessMyDayFeature,
		// Roster query failure keeps role-based My day routing, but ticket scope stays fail closed below.
		errorPolicy: 'all',
	});
	const todayRosterArea = useMemo(() => {
		const shift = (rosterData?.getMyRosterShifts || []).find(rosterShift => rosterShift?.date === todayDateKey);
		return shift ? Number(shift.area) : null;
	}, [rosterData?.getMyRosterShifts, todayDateKey]);
	const assignedRunFilter = useMemo(() => {
		if (!user?.id) {
			return null;
		}

		// Keep the buffered query and exact tenant-local match for compatibility
		// with older server releases that resolve these filters in server-local time.
		return {
			driver: user.id,
			plannedFromDate: getLocalTime(tenant?.timezone).subtract(1, 'day').startOf('day').unix(),
			plannedToDate: getLocalTime(tenant?.timezone).add(1, 'day').endOf('day').unix(),
		};
		// todayDateKey moves the query window forward when the day rolls over.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tenant?.timezone, todayDateKey, user?.id]);
	const {
		data: assignedRunData,
		loading: assignedRunLoading,
		error: assignedRunError,
		refetch: refetchAssignedRuns,
	} = useQuery(GET_MY_DAY_ASSIGNED_RUN_CHECK, {
		variables: {
			sortBy: 'planned',
			sortDirection: 1,
			filter: assignedRunFilter,
		},
		skip: !canAccessMyDayFeature || !assignedRunFilter || isDriver || isPicker,
		// Roles without service run access retain the hub, while ticket selection fails closed below.
		errorPolicy: 'all',
	});
	const hasRunAssignedToday = useMemo(() => (
		(assignedRunData?.getServiceRuns?.items || []).some(run => (
			run?.planned && getLocalTime(tenant?.timezone, run.planned).format('YYYY-MM-DD') === todayDateKey
		))
	), [assignedRunData?.getServiceRuns?.items, tenant?.timezone, todayDateKey]);
	const refetchModeData = useCallback(() => {
		// The user carries extra access, which can be given or removed while My day is open.
		const requests = refetchUser ? [refetchUser()] : [];

		if (isAVC && canAccessMyDayFeature) {
			requests.push(refetchRosterShifts());
		}
		if (canAccessMyDayFeature && assignedRunFilter && !isDriver && !isPicker) {
			requests.push(refetchAssignedRuns());
		}

		return Promise.all(requests);
	}, [
		assignedRunFilter,
		canAccessMyDayFeature,
		isAVC,
		isDriver,
		isPicker,
		refetchAssignedRuns,
		refetchRosterShifts,
		refetchUser,
	]);

	const mode = useMemo(() => {
		if (!canAccessMyDayFeature) {
			return MY_DAY_MODE.CLASSIC;
		}

		if (todayRosterArea === PEOPLE_ROSTER_AREA.PICKER) {
			return MY_DAY_MODE.PICKER;
		}

		if (todayRosterArea === PEOPLE_ROSTER_AREA.DRIVER || todayRosterArea === PEOPLE_ROSTER_AREA.DRIVER_TRAINING) {
			return MY_DAY_MODE.DRIVER;
		}

		if (isDriver) {
			return MY_DAY_MODE.DRIVER;
		}

		if (isPicker) {
			return MY_DAY_MODE.PICKER;
		}

		return hasRunAssignedToday ? MY_DAY_MODE.DRIVER : MY_DAY_MODE.HUB;
	}, [canAccessMyDayFeature, hasRunAssignedToday, isDriver, isPicker, todayRosterArea]);

	const ticketScope = useMemo(() => {
		if (!canAccessMyDayFeature) {
			return MY_DAY_TICKET_SCOPE.GENERIC;
		}

		if (todayRosterArea === PEOPLE_ROSTER_AREA.DRIVER || todayRosterArea === PEOPLE_ROSTER_AREA.DRIVER_TRAINING) {
			return MY_DAY_TICKET_SCOPE.DRIVER;
		}

		if (todayRosterArea === PEOPLE_ROSTER_AREA.PICKER) {
			return MY_DAY_TICKET_SCOPE.GENERIC;
		}

		// Until roster mode is known, never issue the broader generic ticket query.
		if (isAVC && ((rosterLoading && !rosterData) || rosterError)) {
			return MY_DAY_TICKET_SCOPE.DRIVER;
		}

		if (isDriver) {
			return MY_DAY_TICKET_SCOPE.DRIVER;
		}

		if (isPicker) {
			return MY_DAY_TICKET_SCOPE.GENERIC;
		}

		// Assigned-run detection is the final cover-driver decision. Loading and
		// errors use the authenticated server-derived scope rather than fleet data.
		if ((assignedRunLoading && !assignedRunData) || assignedRunError) {
			return MY_DAY_TICKET_SCOPE.DRIVER;
		}

		return hasRunAssignedToday ? MY_DAY_TICKET_SCOPE.DRIVER : MY_DAY_TICKET_SCOPE.GENERIC;
	}, [
		assignedRunData,
		assignedRunError,
		assignedRunLoading,
		canAccessMyDayFeature,
		hasRunAssignedToday,
		isAVC,
		isDriver,
		isPicker,
		rosterData,
		rosterError,
		rosterLoading,
		todayRosterArea,
	]);

	return {
		canAccessMyDayFeature,
		mode,
		refetchModeData,
		ticketScope,
	};
};

export default useMyDayMode;
